import { IGatsbyImageData } from "gatsby-plugin-image"

import { Client } from "shopify-buy"

export enum header_option_types {
  link = "link",
  dropdown = "dropdown",
  external_link = "external-link",
  button = "button",
}

export enum modal_screen_sizes {
  xs = "sm",
  sm = "lg",
  md = "lg",
  lg = "xl",
  xl = "xl",
}

export enum forms {
  contact_us = "contact-us",
}

export enum collection_keys {
  etg = "etg",
  synthetic = "synthetic-drug-tests",
  clia_waived = "clia-waived-drug-tests",
  lab_testing = "laboratory-services",
  best_sellers = "best-selling-drug-tests",
  saliva_drug_test = "saliva-drug-tests",
  urine_drug_test_cup = "urine-drug-test-cups",
  urine_drug_test_dip_card = "urine-drug-test-dip-cards",
  most_popular_drug_tests = "most-popular-drug-tests",
}

export enum metafield_keys {
  panels = "panels",
  training_video = "training_video",
  collection_1 = "collection_1",
  bullet_points = "bullet_points",
  badge_1 = "badge_1",
  badge_2 = "badge_2",
  badge_3 = "badge_3",
  buy_now = "buy_now",
  results_1 = "results_1",
  headline = "headline",
}

export enum product_options {
  panel_size = "Panel Size",
  configuration = "Configuration",
}

export enum text_marks {
  strong = "strong",
  em = "em",
  italic = "italic",
  code = "code",
}

export enum sanity_content {
  companyIntro = "companyIntro",
  quote = "quote",
  featuresBenefits = "featuresBenefits",
  partners = "partners",
  about = "about",
  faqs = "faqs",
  linkButton = "linkButton",
  resources = "resources",
  employeeSection = "employeesSection",
  landingPage = "landingPage",
}

export enum employee_types {
  medical = "medical",
  executive = "executive",
  management = "management",
  boardMember = "boardMember",
  author = "author",
}

export type RichText = {
  text: string
  marks: text_marks[]
  _key: string
  _type: string
}

export type PageWithContextData<T> = {
  pageContext: {
    data: T
  }
}
export type ImageSharp = {
  id: string
  localFile: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

export type ProductOption = {
  id: string
  name: product_options
  values: string[]
}

export type ProductVariant = {
  id: string
  title: string
  price: string
  requiresShipping: boolean
  shopifyId: string
  sku: string
  price: ProductPrice
  availableForSale: boolean
}

export type ProductPrice = {
  amount: string
  currencyCode: string
}

export type Service = {
  text?: string
  href?: string
  image: SanityImage
}

export type Product = {
  handle: string
  priceRange: {
    maxVariantPrice: {
      amount: string
      currencyCode: string
    }
    minVariantPrice: {
      amount: string
      currencyCode: string
    }
  }
  productType: string
  publishedAt: Date
  title: string
  description: string
  descriptionHtml: string
  availableForSale: boolean
  vendor: string
  variants: ProductVariant[]
  options: ProductOption[]
  images: ImageSharp[]
  metafields: Metafield[]
}

export type Metafield = {
  key: metafield_keys
  value: string
}

export type Collection = {
  handle: string
  id: string
  description: string
  title: string
  descriptionHtml: string
  image: ImageSharp
  products: Product[]
  metafields: any;
}

export type ShopifyAdminAPI = {
  collectionByHandle: {
    metafields: {
      edges: {
        node: Metafield
      }[]
    }
  }
  productByHandle: {
    metafields: {
      edges: {
        node: Metafield
      }[]
    }
  }
}

export type ConfirmBanner = {
  title: string
  image: SanityImage
  button: SanityButtonLink
  _rawText: SanityRawText[]
  stores: StoreButtonLink
}

export type SanityImage = {
  alt: string
  asset: {
    gatsbyImageData: IGatsbyImageData
  }
}

export type CutOffChart = {
  title: string
  handle?: Slug
  image: {
    asset: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

export type AllSanityResourcePage = {
  edges: {
    node: Resource
  }[]
}

export type AllGraphQLResponse<T> = {
  edges: {
    node: T
  }[]
}

export type Author = {
  name: string
  slug: Slug
  position: string
  title?: string
}

export type MedReview = {
  title: string
  category: string
  name: string
  slug: Slug
  position: string
  order: number
  image: SanityImage
  bio: SanityText[]
  _rawBio: SanityRawText[]
}

export type SanitySource = {
  href: string
  caption: string
}

export type ResourceCategory = {
  name: string
  slug: Slug
}

export type Resource = {
  _type?: string
  title: string
  slug: Slug
  category: ResourceCategory
  order: number
  author: Employee
  date: string
  medReview: Employee
  image: SanityImage
  content: SanityText[]
  _rawContent: SanityRawText[]
  sources: SanitySource[]
  relatedProducts: SanityProduct[]
  relatedResources: Resource[]
  meta: {
    title: string
    description: string
  }
}

export type PrivacyPolicy = {
  title: string
  _rawContent: SanityRawText[]
}

export type SanityProduct = {
  faqs: FAQ[]
  collectionProcedure: Procedure[]
  howToReadResults: Procedure[]
  trainingVideo: TrainingVideo
  title: string
  productType: string
  textField: SanityRawText[]
  resources: Resource[]
}

export type SanityCollection = {
  title: string
  handle: Slug
  image?: ImageSharp
  faqs: FAQ[]
  services?: Service[]
  textField: SanityRawText[]
  resources: Resource[]
}

export type TrainingVideo = {
  title: string
  content: string
}

export type Procedure = {
  title: string
  image: SanityImage
  text: SanityText[]
  _rawText?: SanityRawText[]
}

export type FAQ = {
  question: string
  answer?: SanityText[]
  _rawAnswer?: SanityRawText[]
}

export type Slug = {
  current: string
}

export type SanitySettings = {
  title: string
  openGraph: {
    description: string
  }
}

export type SanityButtonLink = {
  label: string
  link: string
  _type?: sanity_content
}

export type StoreButtonLink = {
  label: string
  link: string
  image?: any
}

export type SanityText = {
  style: string
  list: any
  children: RichText[]
}
export type SanityRawText = {
  _key: string
  _type: string
  children: RichText[]
  marksDef: any[]
  style: string
}

export type SanityCompanyIntro = {
  _type: sanity_content
  title: string
  image: SanityImage
  text: SanityText[]
  _rawText: SanityRawText[]
}

export type SanityFeatureBenefit = {
  _type: sanity_content.featuresBenefits
  featureBenefit: Feature[]
}

export type Feature = {
  title: string
  image: SanityImage
}

export type SanityPartners = {
  _type: sanity_content.partners
  images: SanityImage[]
}

export type SanityQuote = {
  _type: sanity_content.quote
  text: SanityText[]
  _rawText: SanityRawText[]
  buttonLink: SanityButtonLink
  image: SanityImage
}

export type SanityResource = {
  _type: sanity_content.resources
  resources: Resource[]
}

export type SanityAbout = {
  _rawHtmlBlock: SanityRawText[]
  _type: sanity_content.about
}

export type SanityFaqs = {
  _type: sanity_content.faqs
  faq: FAQ[]
}

export type SanityPage = {
  subtitle: string
  title: string
  image: SanityImage
  backgroundImage: SanityImage
  content: (
    | SanityPartners
    | SanityFeatureBenefit
    | SanityQuote
    | SanityCompanyIntro
    | SanityResource
  )[]
}

export type Employee = {
  title: string
  category: string
  name: string
  slug: Slug
  position: string
  order: number
  image: SanityImage
  bio: SanityText[]
  _rawBio: SanityRawText[]
}

export type CalloutBanner = {
  id: string
  title: string
  button: SanityButtonLink
}

export interface IShopCheckout {
  id?: string | number
  lineItemCount?: number
  subtotalPrice?: string
  totalPrice?: string
  totalTax?: string
  completedAt?: string
  checkoutUrl?: string
  lineItems?: {
    id?: string | number
    variantId: string | number
    quantity: number
    customAttributes?: { key: string; value: string }[]
  }[]
  lineItemsSubtotalPrice?: {
    amount: string
    currencyCode: string
  }
  webUrl?: string
  userErrors?: {
    field: {
      type: {
        kind: string
        name: string
      }
      value: string
    }[]
    message: string
  }[]
  discountApplications?: DiscountApplications[]
}

export type DiscountApplications = {
  allocationMethod: string
  applicable: boolean
  code: string
  value: {
    amount: string
    currencyCode: string
  }
}

export interface IShopContext {
  isLoading: boolean
  isCartOpen: boolean
  quantity: number
  coupon: string
  checkout: IShopCheckout
  toggleCartOpen: () => void
  onCartClose: () => void
  addProductToCart?: (
    variantId: string | number,
    quantity: number
  ) => Promise<void>
  updateQuantity?: (id: string, quantity: number | string) => Promise<void>
  removeProductFromCart?: (id: string) => Promise<void>
  onCouponChange?: (value: string) => void
  checkCoupon?: (coupon: string) => Promise<void>
  removeCoupon?: (coupon: string) => Promise<void>
  client: Client
}

export type SanityLandingPage = {
  _type?: string
  title: string
  subtitle: string
  handle: Slug
  cta: SanityButtonLink
  image: SanityImage
  backgroundImage: SanityImage
  content: (
    | SanityAbout
    | SanityFeatureBenefit
    | SanityPartners
    | SanityQuote
    | SanityButtonLink
    | SanityFaqs
    | SanityResource
    | EmployeeSection
  )[]
}

export type SanitySimplePage = {
  title: string
  handle: Slug
  _rawContent: SanityRawText[]
  employees: EmployeeSection[]
}

export type EmployeeSection = {
  title: string
  employees: Employee[]
  _type?: sanity_content
}
